/**
 * @module hooks/useBreakpoint
 * @summary Reactive layout breakpoint + touch-capability detection.
 * @description Screen size (layout) and pointer capability (interaction) are
 *   tracked as two orthogonal signals — a 1100px touch laptop and a 1000px
 *   mouse window are different cases. See docs/TECHNICAL-GUIDE.md (Responsive).
 */
import { useEffect, useState } from 'react';

export type Breakpoint = 'phone' | 'tablet-portrait' | 'tablet-landscape' | 'desktop';

function computeBp(): Breakpoint {
  const w = window.innerWidth;
  if (w < 600) return 'phone';
  if (w < 900) return 'tablet-portrait';
  if (w < 1200) return 'tablet-landscape';
  return 'desktop';
}

export function useBreakpoint(): { bp: Breakpoint; isSmall: boolean; isTouch: boolean } {
  const [bp, setBp] = useState<Breakpoint>(() =>
    typeof window !== 'undefined' ? computeBp() : 'desktop',
  );
  useEffect(() => {
    const onResize = () => setBp(computeBp());
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);
  const isTouch =
    typeof window !== 'undefined' &&
    (window.matchMedia?.('(pointer: coarse)').matches || navigator.maxTouchPoints > 0);
  return { bp, isSmall: bp === 'phone' || bp === 'tablet-portrait', isTouch };
}
